import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Plus, Pencil, Trash2, LogOut, Star } from "lucide-react";
import { api, inr, formatApiErrorDetail } from "@/api";

const STATUSES = ["New", "Contacted", "Booked", "Cancelled"];
const STATUS_COLORS = { New: "text-sky-300 border-sky-400/40", Contacted: "text-amber-300 border-amber-400/40", Booked: "text-emerald-300 border-emerald-400/40", Cancelled: "text-slate-400 border-white/20" };
const EMPTY_PKG = { name: "", destination: "Bali", country: "", days: 5, nights: 4, price: 0, tag: "", image: "", overview: "", featured: false };

export default function AdminDashboard() {
  const [admin, setAdmin] = useState(null);
  const [tab, setTab] = useState("enquiries");
  const [stats, setStats] = useState(null);
  const [enquiries, setEnquiries] = useState([]);
  const [packages, setPackages] = useState([]);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY_PKG);
  const navigate = useNavigate();

  const load = async () => {
    const [s, e, p] = await Promise.all([
      api.get("/admin/stats"), api.get("/admin/enquiries"), api.get("/packages"),
    ]);
    setStats(s.data);
    setEnquiries(e.data);
    setPackages(p.data);
  };

  useEffect(() => {
    api.get("/auth/me")
      .then((r) => { setAdmin(r.data); load().catch(() => {}); })
      .catch(() => navigate("/admin/login"));
  }, [navigate]);

  const setStatus = async (id, status) => {
    try {
      await api.patch(`/admin/enquiries/${id}`, { status });
      setEnquiries((list) => list.map((x) => (x.id === id ? { ...x, status } : x)));
      toast.success(`Marked as ${status}`);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const openCreate = () => { setForm(EMPTY_PKG); setEditing("new"); };
  const openEdit = (p) => { setForm({ ...EMPTY_PKG, ...p }); setEditing(p.id); };

  const savePackage = async (e) => {
    e.preventDefault();
    const payload = { ...form, days: Number(form.days), nights: Number(form.nights), price: Number(form.price) };
    try {
      if (editing === "new") await api.post("/admin/packages", payload);
      else await api.put(`/admin/packages/${editing}`, payload);
      toast.success(editing === "new" ? "Package created" : "Package updated");
      setEditing(null);
      load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail));
    }
  };

  const removePackage = async (id) => {
    if (!window.confirm("Delete this package permanently?")) return;
    await api.delete(`/admin/packages/${id}`);
    toast.success("Package deleted");
    load();
  };

  const logout = async () => {
    await api.post("/auth/logout").catch(() => {});
    navigate("/admin/login");
  };

  if (!admin) return <div className="min-h-screen flex items-center justify-center text-slate-400">Loading…</div>;

  return (
    <div className="min-h-screen bg-onyx-900" data-testid="admin-dashboard">
      <header className="border-b border-white/10 bg-onyx-800/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-16 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <img src="/logo-mark.jpg" alt="T&T Travels" className="h-9 w-auto rounded-lg" />
            <span className="font-serif text-lg text-slate-50">T&amp;T Admin</span>
          </div>
          <div className="flex items-center gap-4">
            <div className="flex rounded-full border border-white/10 overflow-hidden">
              {["enquiries", "packages"].map((t) => (
                <button key={t} onClick={() => setTab(t)} data-testid={`admin-tab-${t}`}
                  className={`px-5 py-2 text-xs font-mono uppercase tracking-wider transition-colors ${tab === t ? "bg-gold text-onyx-900 font-semibold" : "text-slate-300 hover:text-gold"}`}>
                  {t}
                </button>
              ))}
            </div>
            <button onClick={logout} data-testid="admin-logout-btn" className="flex items-center gap-2 text-xs text-slate-400 hover:text-gold transition-colors">
              <LogOut size={14} /> Logout
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-10">
        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-10" data-testid="admin-stats">
            {[["Total enquiries", stats.total_enquiries], ["New leads", stats.by_status?.New ?? 0], ["Booked", stats.by_status?.Booked ?? 0], ["Live packages", stats.total_packages]].map(([label, val]) => (
              <div key={label} className="rounded-2xl border border-white/10 bg-onyx-800 p-5">
                <div className="font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">{label}</div>
                <div className="mt-2 font-serif text-3xl text-gold">{val}</div>
              </div>
            ))}
          </div>
        )}

        {tab === "enquiries" && (
          <div className="rounded-2xl border border-white/10 bg-onyx-800 overflow-hidden" data-testid="admin-enquiries-table">
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead>
                  <tr className="border-b border-white/10 text-left font-mono text-[10px] uppercase tracking-[0.2em] text-slate-500">
                    <th className="px-5 py-4">Date</th><th className="px-5 py-4">Traveller</th><th className="px-5 py-4">Contact</th>
                    <th className="px-5 py-4">Package</th><th className="px-5 py-4">Travel</th><th className="px-5 py-4">Status</th>
                  </tr>
                </thead>
                <tbody>
                  {enquiries.map((q, i) => (
                    <tr key={q.id} className="border-b border-white/5 hover:bg-white/[0.02]">
                      <td className="px-5 py-4 text-slate-400 whitespace-nowrap">{q.created_at ? new Date(q.created_at).toLocaleDateString("en-IN", { day: "numeric", month: "short" }) : "—"}</td>
                      <td className="px-5 py-4 text-slate-200">{q.name}<div className="text-xs text-slate-500">{q.travelers} traveller(s)</div></td>
                      <td className="px-5 py-4 text-slate-400 text-xs">{q.phone}<div>{q.email}</div></td>
                      <td className="px-5 py-4 text-slate-300 text-xs max-w-[200px]">{q.package_name || q.destination || "Custom trip"}</td>
                      <td className="px-5 py-4 text-slate-400 text-xs whitespace-nowrap">{q.travel_date || "Flexible"}</td>
                      <td className="px-5 py-4">
                        <select value={q.status} onChange={(e) => setStatus(q.id, e.target.value)} data-testid={`enquiry-status-toggle-${i}`}
                          className={`rounded-full bg-onyx-700 border px-3 py-1.5 text-xs focus:outline-none ${STATUS_COLORS[q.status] || ""}`}>
                          {STATUSES.map((s) => <option key={s} value={s}>{s}</option>)}
                        </select>
                      </td>
                    </tr>
                  ))}
                  {enquiries.length === 0 && (
                    <tr><td colSpan={6} className="px-5 py-12 text-center text-slate-500">No enquiries yet — they will appear here as travellers write in.</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {tab === "packages" && (
          <div data-testid="admin-packages-panel">
            <div className="flex justify-end mb-5">
              <button onClick={openCreate} data-testid="admin-add-package-btn"
                className="flex items-center gap-2 rounded-full bg-gold px-5 py-2.5 text-xs font-semibold text-onyx-900 hover:bg-gold-light transition-colors">
                <Plus size={14} /> New package
              </button>
            </div>
            <div className="grid md:grid-cols-2 gap-4">
              {packages.map((p, i) => (
                <div key={p.id} className="rounded-2xl border border-white/10 bg-onyx-800 p-5 flex gap-4" data-testid={`admin-package-row-${i}`}>
                  <img src={p.image} alt="" className="h-20 w-28 rounded-xl object-cover border border-white/10" />
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <h3 className="font-serif text-lg text-slate-50 truncate">{p.name}</h3>
                      {p.featured && <Star size={13} className="fill-gold text-gold shrink-0" />}
                    </div>
                    <div className="mt-1 text-xs text-slate-400 font-mono uppercase tracking-wider">{p.destination} · {p.nights}N/{p.days}D · <span className="text-gold">{inr(p.price)}</span></div>
                    <div className="mt-3 flex gap-2">
                      <button onClick={() => openEdit(p)} data-testid={`admin-edit-package-${i}`} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:border-gold/50 hover:text-gold transition-colors"><Pencil size={12} /> Edit</button>
                      <button onClick={() => removePackage(p.id)} data-testid={`admin-delete-package-${i}`} className="flex items-center gap-1.5 rounded-full border border-white/15 px-3 py-1.5 text-xs text-slate-300 hover:border-red-400/50 hover:text-red-400 transition-colors"><Trash2 size={12} /> Delete</button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>

      {editing && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-4" data-testid="package-edit-modal">
          <div className="absolute inset-0 bg-onyx-900/70 backdrop-blur-sm" onClick={() => setEditing(null)} />
          <form onSubmit={savePackage} className="relative w-full max-w-lg rounded-2xl border border-white/10 bg-onyx-800 p-7 space-y-4 max-h-[85vh] overflow-y-auto">
            <h3 className="font-serif text-2xl text-slate-50">{editing === "new" ? "New package" : "Edit package"}</h3>
            <input required placeholder="Package name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="pkg-form-name" className="input-dark" />
            <div className="grid grid-cols-2 gap-4">
              <input required placeholder="Destination (e.g. Bali)" value={form.destination} onChange={(e) => setForm({ ...form, destination: e.target.value })} data-testid="pkg-form-destination" className="input-dark" />
              <input placeholder="Country" value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} className="input-dark" />
            </div>
            <div className="grid grid-cols-3 gap-4">
              <label className="block"><span className="text-xs text-slate-400">Days</span>
                <input type="number" min="1" value={form.days} onChange={(e) => setForm({ ...form, days: e.target.value })} className="input-dark mt-1" /></label>
              <label className="block"><span className="text-xs text-slate-400">Nights</span>
                <input type="number" min="0" value={form.nights} onChange={(e) => setForm({ ...form, nights: e.target.value })} className="input-dark mt-1" /></label>
              <label className="block"><span className="text-xs text-slate-400">Price ₹</span>
                <input type="number" min="0" required value={form.price} onChange={(e) => setForm({ ...form, price: e.target.value })} data-testid="pkg-form-price" className="input-dark mt-1" /></label>
            </div>
            <input placeholder="Tag (Honeymoon, Family…)" value={form.tag} onChange={(e) => setForm({ ...form, tag: e.target.value })} className="input-dark" />
            <input placeholder="Image URL" value={form.image} onChange={(e) => setForm({ ...form, image: e.target.value })} className="input-dark" />
            <textarea rows={3} placeholder="Overview" value={form.overview} onChange={(e) => setForm({ ...form, overview: e.target.value })} className="input-dark resize-none" />
            <label className="flex items-center gap-3 text-sm text-slate-300">
              <input type="checkbox" checked={form.featured} onChange={(e) => setForm({ ...form, featured: e.target.checked })} data-testid="pkg-form-featured" className="accent-[#D4AF37] h-4 w-4" />
              Mark as Bestseller
            </label>
            <div className="flex gap-3 pt-2">
              <button type="submit" data-testid="pkg-form-save" className="flex-1 rounded-full bg-gold py-3 text-sm font-semibold text-onyx-900 hover:bg-gold-light transition-colors">Save</button>
              <button type="button" onClick={() => setEditing(null)} data-testid="pkg-form-cancel" className="rounded-full border border-white/15 px-6 text-sm text-slate-300 hover:border-gold/40 transition-colors">Cancel</button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
